import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { ExternalLinkIcon, XIcon } from 'lucide-react';
interface EmbedWindowProps {
  url: string;
  title: string;
  onClose: () => void;
}

/** A live web page embedded in a browser-style window above the presentation. */
export function EmbedWindow({
  url,
  title,
  onClose
}: EmbedWindowProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  return createPortal(<motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25
  }} className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8" data-id="element-236" data-mp="1do73t6uoavin">
      <button type="button" aria-label="Close window" onClick={onClose} className="absolute inset-0 cursor-default bg-[var(--fg)]/60 focus:outline-none" data-id="element-237" data-mp="18sp39ua9old1" />
      <motion.div role="dialog" aria-modal="true" aria-label={title} initial={{
      opacity: 0,
      scale: 0.95,
      y: 16
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} exit={{
      opacity: 0,
      scale: 0.97,
      y: 10
    }} transition={{
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1]
    }} className="relative flex h-[min(90vh,900px)] w-[min(1240px,100%)] flex-col overflow-hidden rounded-[20px] bg-white shadow-[0_60px_120px_-40px_rgba(0,0,0,0.7)]" data-id="element-238" data-mp="z248khxps0ze">
        <div className="flex shrink-0 items-center gap-3 border-b border-[var(--fg)]/[0.08] px-4 py-2.5" data-id="element-239" data-mp="b71gm13m5hep">
          <div className="flex gap-1.5" aria-hidden="true" data-id="element-240" data-mp="2aic25tv641yx">
            <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-241" data-mp="25j98t6ojvk6s" />
            <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-242" data-mp="3ulgvwr7dwny" />
            <span className="h-2.5 w-2.5 rounded-full bg-[var(--fg)]/15" data-id="element-243" data-mp="3taea09huuyf" />
          </div>
          <span className="truncate rounded-full bg-[var(--surface)] px-3 py-1 text-[10.5px] font-medium text-[var(--muted)]" data-id="element-244" data-mp="8mv1cx9ju4iu">
            {url.replace(/^https?:\/\//, '')}
          </span>
          <a href={url} target="_blank" rel="noopener noreferrer" className="ml-auto inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[11px] font-semibold text-[var(--brand)] transition-colors hover:bg-[var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]" data-id="element-245" data-mp="3v1o4yfbqvmty">
            New tab
            <ExternalLinkIcon className="h-3 w-3" aria-hidden="true" data-id="element-246" data-mp="hkypm1bx5fec" />
          </a>
          <button type="button" onClick={onClose} className="inline-flex h-8 items-center gap-1.5 rounded-full bg-[var(--surface)] px-3 text-[11px] font-semibold text-[var(--fg)] transition-colors hover:bg-[#EBE6DB] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)]" data-id="element-247" data-mp="2xcuxhj3hw35p">
            Close
            <XIcon className="h-3 w-3" aria-hidden="true" data-id="element-248" data-mp="1fhlxjho3chbr" />
          </button>
        </div>
        <iframe src={url} title={title} className="min-h-0 w-full flex-1 border-0 bg-white" allow="fullscreen" data-id="element-249" data-mp="1td6dc48iwjey" />
      </motion.div>
    </motion.div>, document.body);
}